import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'cookieConsent';

const defaultConsent = {
  necessary: true,
  preferences: false,
  analytics: false,
  saved: false
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [consent, setConsent] = useState(defaultConsent);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setConsent({ ...defaultConsent, ...parsed });
        if (parsed.saved) return; // already saved -> don't show
      }
    } catch (e) {
      // ignore
    }
    // show banner if not saved
    setVisible(true);
  }, []);

  const saveConsent = (newConsent) => {
    const toSave = { ...defaultConsent, ...newConsent, saved: true };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    setConsent(toSave);
    setVisible(false);
    // notify gate (if any) that consent changed
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };
  const acceptAll = () => saveConsent({ necessary: true, preferences: true, analytics: true });
  const rejectAll = () => saveConsent({ necessary: true, preferences: false, analytics: false });

  const toggle = (key) => setConsent(prev => ({ ...prev, [key]: !prev[key] }));

  // compact pop-in state vs expanded preferences modal
  const [modalOpen, setModalOpen] = useState(false);

  if (!visible) return null;

  return (
    <>
      {/* Compact bottom-right pop-in */}
      {!modalOpen && (
        <div className="fixed left-4 bottom-6 z-50 pointer-events-auto">
          <div className="w-80 bg-white dark:bg-gray-800 text-black dark:text-white rounded-xl shadow-lg p-4 flex flex-col">
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-3">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" className="text-primary">
                  <circle cx="18" cy="18" r="15" fill="currentColor" opacity="0.08" />
                  <path d="M12 18l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold">PulseCIO — Cookies</div>
                <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">Nous utilisons des cookies nécessaires et optionnels pour améliorer votre expérience.</div>
                <div className="mt-2 flex items-center space-x-2">
                  <button onClick={acceptAll} className="flex-1 px-3 py-2 bg-primary dark:text-black text-white rounded-md text-sm">ACCEPTER</button>
                  <button onClick={rejectAll} className="px-3 py-2 text-sm rounded-md border" title="Refuser les cookies optionnels">NON</button>
                </div>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <button onClick={() => setModalOpen(true)} className="underline">Préférences</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modern preferences modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-gray-900 text-black dark:text-white rounded-2xl shadow-2xl w-full max-w-2xl p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-bold">Préférences des cookies</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Personnalisez les cookies que vous autorisez. Les cookies nécessaires sont indispensables au fonctionnement du site.</p>
              </div>
              <button onClick={() => setModalOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg border-primary/20 dark:border-gray-700">
                <div className="flex items-start">
                  <input type="checkbox" checked disabled className="mr-3 mt-1" />
                  <div>
                    <div className="font-semibold">Nécessaires</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Cookies indispensables (session, sécurité, authentification).</div>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg border-primary/20 dark:border-gray-700">
                <div className="flex items-start">
                  <input type="checkbox" checked={consent.preferences} onChange={() => toggle('preferences')} className="mr-3 mt-1" />
                  <div>
                    <div className="font-semibold">Préférences</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Enregistrer vos choix d'affichage et préférences UX.</div>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg md:col-span-2 border-primary/20 dark:border-gray-700">
                <div className="flex items-start">
                  <input type="checkbox" checked={consent.analytics} onChange={() => toggle('analytics')} className="mr-3 mt-1" />
                  <div>
                    <div className="font-semibold">Analytics</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">Collecte d'usage anonyme pour améliorer le service.</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button onClick={() => { saveConsent({ necessary: true, preferences: consent.preferences, analytics: consent.analytics }); setModalOpen(false); }} className="px-4 py-2 bg-primary dark:text-black text-white rounded-md">Enregistrer</button>
              <button onClick={() => { setConsent(defaultConsent); saveConsent(defaultConsent); setModalOpen(false); }} className="px-4 py-2 border rounded-md">Réinitialiser</button>
              <button onClick={() => { setModalOpen(false); }} className="px-4 py-2 text-sm">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
